import * as React from "react";
import { MouseEvent, useEffect, useMemo, useState } from "react";
import { useSelector, useStore } from "react-redux";

import NumberFormat from "react-number-format";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Alert, Slide, ToggleButton, ToggleButtonGroup } from "@mui/material";

import { getTotal } from "../utils/index.ts";
import {setTotals } from "../Store/roiStore.ts";
import {
    CUSTOMER_SITUATION,
    FACILITY_STATUS,
    SKU_REDUCTION_KEY,
    existingScaleCoverage,
    isExistingCustomerMode,
    resolveCustomerSituation,
    resolveFacilityScales,
    situationTitle,
    sumFacilityScales,
} from "../utils/customerSituation.ts";
import { hasPar360Solution } from "../constants.ts";
import "../styles/pages/roi-calculations.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const formatCurrency = (value: number) => {
    const safe = Number.isFinite(value) ? value : 0;
    const abs = Math.abs(safe);
    const formatted = `$${abs.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
    return safe < 0 ? `-${formatted}` : formatted;
};

const displayNumber = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const yearColumnSx = (colIndex: number) => ({
    borderLeft: colIndex > 0 ? "2px solid #e0e0e0" : undefined,
    backgroundColor: colIndex > 0 && colIndex % 2 === 0 ? "#f8f9fa" : undefined,
});

export const ROICalculations = () => {
    const SUPPLY_EXPENSE_INCREASE_KEY = "Anticipated Annual Supply Expense Increase";
    const CLIENT_SUPPLY_COST_KEY = "Estimated Average Annual Supply Cost Increase";
    const OVERSTOCK_IMPACT_KEY = "Impact of PAR on Overstock";
    const EXPIRATION_IMPACT_KEY = "Impact of PAR on Expiration";
    const CALLDOWN_REDUCTION_KEY = "Call Down Reduction";
    const FREIGHT_SPEND_KEY = "Annual Freight Spend";
    const FREIGHT_SAVINGS_PCT_KEY = "Freight Savings %";
    const PAR360_PREMIUM = "PAR 360 Premium";
    const { dispatch } = useStore();
    const [roiTerm, setRoiTerm] = useState(5);
    const [chartView, setChartView] = useState<"annual" | "cumulative">("annual");
    const totalsInStore = useSelector((state) => state.inputFields.totals);
    const laborcost = useSelector((state) => state.inputFields.laborCost);

    const spendDetailsDataFromStore = useSelector(
        (state) => state.inputFields.spendDetailsRows
    );

    const clientCostsFromStore = useSelector(
        (state) => state.inputFields.clientCosts
    );

    const investmentSummaryFromStore = useSelector(
        (state) => state.inputFields.investmentSummary
    );

    const clientFromStore = useSelector(
        (state) => state.inputFields.client
    );

    // Freight ("Bags Delivery") savings are only offered with PAR 360 Premium.
    const isPar360Premium = (clientFromStore?.currentParSolutions ?? []).includes(PAR360_PREMIUM);
    // Incremental add-on: existing scaled customer adding PAR 360. Scale-driven
    // savings (cycle count labor, order lines) and existing scale subscription
    // are excluded so the ROI only measures new PAR 360 value vs new PAR 360 cost.
    const situation = resolveCustomerSituation(clientFromStore);
    const existingMode = isExistingCustomerMode(situation);
    const isPar360AddOn = situation === CUSTOMER_SITUATION.PAR360_ADDON;
    const par360Selected = hasPar360Solution(clientFromStore?.currentParSolutions);
    const scaleSplitTotals = sumFacilityScales(spendDetailsDataFromStore, situation);
    const newScaleCount = scaleSplitTotals.newScales;
    const zeroYears = () => Array.from({ length: roiTerm }, () => 0);

    // Legacy ROIs entered PAR fees as monthly amounts (×12 to annualize); new ROIs
    // enter annual amounts directly. Read cadence-specific keys and scale factor so
    // both reopen with correct figures.
    const isLegacyRoi = useSelector((state) => !!state.inputFields.isLegacyRoi);
    const periodsPerYear = isLegacyRoi ? 12 : 1;
    const scaleSubscriptionKey = isLegacyRoi ? "Monthly Scale Subscription Rate" : "Annual Scale Subscription Rate";
    const par360SubscriptionPrefix = isLegacyRoi ? "PAR 360 Plus Monthly Subscription Fee|" : "PAR 360 Plus Annual Subscription Fee|";

    const investmentTotal = Number(totalsInStore.investment ?? 0);
    const scaleSubscription = Number(investmentSummaryFromStore[scaleSubscriptionKey] ?? 0);
    const par360Subscription = Object.keys(investmentSummaryFromStore)
        .filter(key => key.startsWith(par360SubscriptionPrefix))
        .reduce((sum, key) => sum + Number(investmentSummaryFromStore[key] ?? 0), 0);
    const annualSubscriptionCost = (scaleSubscription + par360Subscription) * periodsPerYear;
    const annualSupportEst = Number(investmentSummaryFromStore["Annual Recurring Support Fees (est.)"] ?? 0) || annualSubscriptionCost;
    const supplyExpenseIncreasePct = Number(
        clientCostsFromStore?.[CLIENT_SUPPLY_COST_KEY]
        || investmentSummaryFromStore[SUPPLY_EXPENSE_INCREASE_KEY]
        || totalsInStore.annualSupplyExpenseIncreasePct
        || 4.5
    );
    const overstockImpactPct = Number(investmentSummaryFromStore[OVERSTOCK_IMPACT_KEY] ?? totalsInStore.overstockImpactPct ?? 20);
    const expirationImpactPct = Number(investmentSummaryFromStore[EXPIRATION_IMPACT_KEY] ?? totalsInStore.expirationImpactPct ?? 20);
    const callDownReductionPct = Number(investmentSummaryFromStore[CALLDOWN_REDUCTION_KEY] ?? totalsInStore.callDownReductionPct ?? 20);
    const totalScalCount = totalsInStore.scaleCount ?? 0;
    const solutionSubscriptionsJson = investmentSummaryFromStore["solutionSubscriptions"] ?? "{}";
    const servicePricingDisplay = investmentSummaryFromStore["servicePricingDisplay"] || "aggregate";
    const overstockRecoveryYears = Number(investmentSummaryFromStore["overstockRecoveryYears"] ?? 3);
    const totalSKUs = getTotal("Total SKUs", spendDetailsDataFromStore) ?? 0;
    const parScalePercentage = totalSKUs > 0 ? totalScalCount / totalSKUs : 0;
    const numberOfLocations = Object.keys(spendDetailsDataFromStore).length;
    const inventoryCountFrequencyPerWeekAverage =
        getTotal("Inventory Count Frequency per Week", spendDetailsDataFromStore) /
        numberOfLocations;

    const techBenefits = laborcost["Inventory Tech"]?.["Benefits"] ?? 0;
    const techHourlyRate = laborcost["Inventory Tech"]?.["Hourly Rate"] ?? 0;
    const avgSKU =
        Number(clientCostsFromStore?.["Avg SKU Coverage Per Hour"] ?? laborcost["Inventory Tech"]?.["Avg. SKU coverage per hour during daily scanning activities"] ?? 0);
    const techLoadedHourlyRate = techHourlyRate * (1 + techBenefits / 100);
    const spendRows = Object.keys(spendDetailsDataFromStore);

    // Returns the effective total supply spend (Medsurg + Direct Purchases) for a row,
    // falling back to the legacy "Annual Supply Spend" field for older saved ROIs.
    const getRowSupplySpend = (row: any): number => {
        const medsurg = Number(row?.["Annual Medsurg Spend"] ?? 0);
        const direct = Number(row?.["Annual Direct Purchase Spend"] ?? 0);
        return (medsurg + direct) || Number(row?.["Annual Supply Spend"] ?? 0);
    };

    const totalAnnualSupplySpend = spendRows.reduce(
        (sum, key) => sum + getRowSupplySpend(spendDetailsDataFromStore[key]),
        0
    );

    // Supply spend not already covered by existing scales; overstock and
    // expiration savings are credited on this share only.
    const getRowUncoveredSpend = (row: any): number =>
        getRowSupplySpend(row) * (1 - existingScaleCoverage(row, situation));
    const totalUncoveredSupplySpend = spendRows.reduce(
        (sum, key) => sum + getRowUncoveredSpend(spendDetailsDataFromStore[key]),
        0
    );
    const existingScaleCoveragePct = totalAnnualSupplySpend > 0
        ? (1 - totalUncoveredSupplySpend / totalAnnualSupplySpend) * 100
        : 0;

    const supplyGrowthRate = supplyExpenseIncreasePct / 100;
    const overstockImpactRate = overstockImpactPct / 100;
    const expirationImpactRate = expirationImpactPct / 100;
    const callDownReductionRate = callDownReductionPct / 100;

    const getCycleCountLaborSavings = () => {
        // Already captured by existing PAR Scales — not incremental PAR 360 value.
        if (isPar360AddOn) return zeroYears();

        const cycleCountLaborSavingsYear1 = existingMode
            ? spendRows.reduce((sum, key) => {
                const facility = spendDetailsDataFromStore[key];
                const { newScales } = resolveFacilityScales(facility, situation);
                const freq = Number(facility?.["Inventory Count Frequency per Week"] ?? 0);
                if (avgSKU <= 0 || newScales <= 0) return sum;
                return sum + (newScales / avgSKU) * techLoadedHourlyRate * freq * 52;
            }, 0)
            : (totalScalCount / avgSKU) *
                techLoadedHourlyRate *
                inventoryCountFrequencyPerWeekAverage *
                52;

        let cycleCountLaborSavings = [];
        cycleCountLaborSavings.push(cycleCountLaborSavingsYear1);
        for (let i = 1; i < roiTerm; i++) {
            cycleCountLaborSavings.push(cycleCountLaborSavings[i - 1] * (1 + supplyGrowthRate));
        }
        return cycleCountLaborSavings;
    };

    const getCallDownLaborSavings = () => {
        let locationSumYear1 = 0.0;

        if (numberOfLocations > 0) {
            for (var i = 1; i < numberOfLocations + 1; i++) {
                const dailyCallDowns = Number(spendDetailsDataFromStore[`row` + i]?.["Estimated Monthly Call Downs"] ?? 0);
                const responseMinutes = Number(spendDetailsDataFromStore[`row` + i]?.["Call Down Response Time"] ?? 0);
                const annualCallDownHours = (dailyCallDowns * responseMinutes * 5 * 52) / 60;
                locationSumYear1 += annualCallDownHours * techLoadedHourlyRate * callDownReductionRate;
            }
        }

        let callDownLaborSavings = [];
        callDownLaborSavings.push(locationSumYear1);
        for (let i = 1; i < roiTerm; i++) {
            callDownLaborSavings.push(callDownLaborSavings[i - 1] * (1 + supplyGrowthRate));
        }
        return callDownLaborSavings;
    };

    const costOfCapitalRate = Number(clientCostsFromStore?.["Cost of Capital"] ?? 0) / 100;

    const DELAY_COST_THRESHOLD = 5_000_000;
    const annualOverstockValue = totalUncoveredSupplySpend * (Number(clientCostsFromStore?.["Overstock"] ?? 0) / 100);
    const annualExpirationValue = totalUncoveredSupplySpend * (Number(clientCostsFromStore?.["Product Expiration Rate"] ?? 0) / 100);
    const totalOverstockExpirationOpportunity = annualOverstockValue + annualExpirationValue;
    const delayCostPerDay = costOfCapitalRate > 0
        ? totalOverstockExpirationOpportunity * costOfCapitalRate / 365
        : 0;
    const showDelayCostCallout = totalOverstockExpirationOpportunity >= DELAY_COST_THRESHOLD && delayCostPerDay > 0;

    const getOverstockReduction = () => {
        // Formula mirrors the Inova 2 Overstock worksheet provided by the customer:
        //   - Year 1: overstock / N (no cost of capital yet; no prior-year overstock being held)
        //   - Years 2..N (recovery):   overstock * (1+g)^i * (1/N + CoC)
        //   - Years > N (post-recovery): overstock * (1+g)^i * (1 + CoC) - overstock
        // where i = year - 1, g = annual supply/product cost increase, CoC = annual cost of
        // capital, and N = term to capture the overstock. All upstream rates are coerced to
        // finite numbers so saved ROIs with missing or malformed inputs render $0 instead of
        // leaving cells blank in the UI and PDF.
        const safeNum = (value: unknown, fallback = 0): number => {
            const n = Number(value);
            return Number.isFinite(n) ? n : fallback;
        };

        const centralOverstockPct = safeNum(clientCostsFromStore?.["Overstock"]);
        const supplySpendByOverstock = spendRows.reduce((sum, key) => {
            const spend = getRowUncoveredSpend(spendDetailsDataFromStore[key]);
            return sum + spend * (centralOverstockPct / 100);
        }, 0);

        const totalOverstock = supplySpendByOverstock * safeNum(overstockImpactRate);
        const g = safeNum(supplyGrowthRate);
        const coc = safeNum(costOfCapitalRate);
        const n = Math.max(1, Math.trunc(safeNum(overstockRecoveryYears, 1)));

        const result: number[] = [];
        for (let i = 0; i < roiTerm; i++) {
            const inflated = totalOverstock * Math.pow(1 + g, i);
            let yearValue: number;
            if (i === 0) {
                yearValue = totalOverstock / n;
            } else if (i < n) {
                yearValue = inflated * (1 / n + coc);
            } else {
                yearValue = inflated * (1 + coc) - totalOverstock;
            }
            result.push(safeNum(yearValue));
        }
        return result;
    };

    const getExpirationAvoidance = () => {
        const centralExpirationPct = Number(clientCostsFromStore?.["Product Expiration Rate"] ?? 0);
        const supplySpendByProductExpiration = spendRows
            .map((key) => getRowUncoveredSpend(spendDetailsDataFromStore[key]) * (centralExpirationPct / 100))
            .reduce((a, b) => a + b);

        let expirationReduction = supplySpendByProductExpiration * expirationImpactRate;
        let result = [];
        for (let i = 0; i < roiTerm; i++) {
            if (i === 0) {
                result.push(expirationReduction);
            } else {
                expirationReduction = expirationReduction * (1 + supplyGrowthRate);
                result.push(expirationReduction);
            }
        }
        return result;
    };

    const getOrderLineAvoidance = () => {
        // Order-line / cost-per-pick savings are delivered by scale automation of
        // the ordering process — already in the baseline where scales exist.
        if (isPar360AddOn) return zeroYears();

        const avoidedPercentatge = 0.15;
        const centralCostPerPickLine = Number(clientCostsFromStore?.["Cost per Pick/Line"] ?? 0);
        const avoidedLinesPickedPerMonthCost = spendRows
            .map((key) => {
                const facility = spendDetailsDataFromStore[key];
                const split = resolveFacilityScales(facility, situation);
                const lines = parseInt(facility?.["Lines Picked per Month"] ?? 0, 10) || 0;
                let share = 1;
                if (existingMode && split.status === FACILITY_STATUS.ALREADY_SCALED) {
                    share = split.totalScales > 0 ? split.newScales / split.totalScales : 0;
                }
                return lines * avoidedPercentatge * centralCostPerPickLine * share;
            })
            .reduce((a, b) => a + b);

        const year1 = avoidedLinesPickedPerMonthCost * 12;

        let orderLineAvoidanceYears = [];
        orderLineAvoidanceYears.push(year1);
        for (let i = 1; i < roiTerm; i++) {
            orderLineAvoidanceYears.push(orderLineAvoidanceYears[i - 1] * (1 + supplyGrowthRate));
        }

        return orderLineAvoidanceYears;
    };

    const getInventoryFeeAvoidance = () => {
        const monthlyInventorySystemFees = spendRows
            .map((key) =>
                Number(
                    spendDetailsDataFromStore[key][
                    "Current Monthly Inventory System Fees"
                    ] ?? 0
                )
            )
            .reduce((a, b) => a + b);

        const annualInventoryCost = spendRows
            .map((key) =>
                Number(
                    spendDetailsDataFromStore[key][
                    "Est. Cost of Annual Inventory"
                    ] ?? 0
                )
            )
            .reduce((a, b) => a + b);

        let year1 = (monthlyInventorySystemFees * 12) + annualInventoryCost;
        let inventoryFeeAvoidanceYears = []
        for (var i = 0; i < roiTerm; i++) {
            if (i === 0)
                inventoryFeeAvoidanceYears.push(year1);
            else {
                year1 = year1 * (1 + supplyGrowthRate);
                inventoryFeeAvoidanceYears.push(year1);
            }
        }
        return inventoryFeeAvoidanceYears;
    };

    const getFreightSavings = () => {
        // Flat annual savings = agreed % of the customer's annual freight spend.
        // Gated on PAR 360 Premium; otherwise it contributes nothing.
        const spend = Number(investmentSummaryFromStore[FREIGHT_SPEND_KEY] ?? 0);
        const pct = Number(investmentSummaryFromStore[FREIGHT_SAVINGS_PCT_KEY] ?? 0);
        const annual = isPar360Premium && Number.isFinite(spend) && Number.isFinite(pct)
            ? spend * (pct / 100)
            : 0;
        return Array.from({ length: roiTerm }, () => annual);
    };

    const getSkuReductionSavings = () => {
        const pct = Number(investmentSummaryFromStore[SKU_REDUCTION_KEY] ?? 0);
        const year1 = par360Selected && Number.isFinite(pct) && pct > 0
            ? totalAnnualSupplySpend * (pct / 100)
            : 0;
        const result = [year1];
        for (let i = 1; i < roiTerm; i++) {
            result.push(result[i - 1] * (1 + supplyGrowthRate));
        }
        return result;
    };

    const getSavingsTotals = () => {
        let totals = [];
        for (let i = 0; i < roiTerm; i++) {
            totals.push(
                getCycleCountLaborSavings()[i] +
                getCallDownLaborSavings()[i] +
                getOverstockReduction()[i] +
                getExpirationAvoidance()[i] +
                getOrderLineAvoidance()[i] +
                getInventoryFeeAvoidance()[i] +
                getFreightSavings()[i] +
                getSkuReductionSavings()[i]
            );
        }
        return totals;
    };

    const getFacilityBreakdown = () => {
        const centralOverstockPct = Number(clientCostsFromStore?.["Overstock"] ?? 0);
        const centralExpirationPct = Number(clientCostsFromStore?.["Product Expiration Rate"] ?? 0);
        const centralCostPerPickLine = Number(clientCostsFromStore?.["Cost per Pick/Line"] ?? 0);
        const avoidedPercentage = 0.15;

        return spendRows.map((key) => {
            const facility = spendDetailsDataFromStore[key];
            const name = facility?.["Facility Name"] ?? facility?.Location ?? key;

            const split = resolveFacilityScales(facility, situation);
            const inventoryCountFreq = Number(facility?.["Inventory Count Frequency per Week"] ?? 0);
            const cycleCount = isPar360AddOn || avgSKU <= 0 || split.newScales <= 0
                ? 0
                : (split.newScales / avgSKU) * techLoadedHourlyRate * inventoryCountFreq * 52;

            const dailyCallDowns = Number(facility?.["Estimated Monthly Call Downs"] ?? 0);
            const responseTime = Number(facility?.["Call Down Response Time"] ?? 0);
            const annualCallDownHours = (dailyCallDowns * responseTime * 5 * 52) / 60;
            const callDown = annualCallDownHours * techLoadedHourlyRate * callDownReductionRate;

            const annualSupplySpend = getRowUncoveredSpend(facility);
            const overstockReduction = annualSupplySpend * (centralOverstockPct / 100) * overstockImpactRate;
            const expirationReduction = annualSupplySpend * (centralExpirationPct / 100) * expirationImpactRate;

            const linesPicked = Number(facility?.["Lines Picked per Month"] ?? 0);
            const orderShare = !existingMode || split.status === FACILITY_STATUS.UNSCALED
                ? 1
                : (split.totalScales > 0 ? split.newScales / split.totalScales : 0);
            const orderLine = linesPicked * avoidedPercentage * centralCostPerPickLine * 12 * orderShare;

            const monthlyFees = Number(facility?.["Current Monthly Inventory System Fees"] ?? 0);
            const annualInventoryCost = Number(facility?.["Est. Cost of Annual Inventory"] ?? 0);
            const facilityTotalSKUs = Number(facility?.["Total SKUs"] ?? 0);
            const inventoryFee = (monthlyFees * 12) + annualInventoryCost;

            const total = cycleCount + callDown + overstockReduction + expirationReduction + orderLine + inventoryFee;

            return { name, cycleCount, callDown, overstockReduction, expirationReduction, orderLine, inventoryFee, total };
        });
    };

    const getUpfrontInvestment = () => {
        return Array.from({ length: roiTerm }, (_, i) => i === 0 ? investmentTotal : 0);
    };

    const getAnnualSupport = () => {
        let annualSupport = [];
        annualSupport.push(annualSupportEst);
        for (let i = 1; i < roiTerm; i++) {
            annualSupport.push(annualSupport[i - 1] * 1.05);
        }
        return annualSupport;
    };

    const getAnnualExpense = () => {
        return getUpfrontInvestment().map(
            (item, index) => item + getAnnualSupport()[index]
        );
    };

    const getAnnualROI = () => {
        return getAnnualExpense().map(
            (item, index) => getSavingsTotals()[index] - item ?? 0
        );
    };

    const getTermNetBenefit = () => {
        return getAnnualROI().reduce((sum, value) => sum + (value ?? 0), 0);
    };

    const getAnnualRoiPctYear1 = () => {
        const year1Cost = getAnnualExpense()?.[0] ?? 0;
        if (year1Cost <= 0) {
            return 0;
        }
        return ((getAnnualROI()?.[0] ?? 0) / year1Cost) * 100;
    };

    const getFeesAsPercentSpend = () => {
        const totalAnnualSpend = spendRows
            .map((key) => getRowSupplySpend(spendDetailsDataFromStore[key]))
            .reduce((a, b) => a + b, 0);
        if (totalAnnualSpend <= 0) {
            return 0;
        }
        return ((getAnnualExpense()?.[0] ?? 0) / totalAnnualSpend) * 100;
    };

    const getPaybackMonths = () => {
        let cumulativeNetBenefit = investmentTotal * -1;
        let monthCounter = 0;

        for (let i = 0; i < roiTerm; i++) {
            const monthlyNetBenefit = (getAnnualROI()[i] ?? 0) / 12;

            for (let j = 0; j < 12; j++) {
                cumulativeNetBenefit += monthlyNetBenefit;
                monthCounter++;
                if (cumulativeNetBenefit >= 0) {
                    return monthCounter;
                }
            }
        }

        return null;
    };

    const facilityBreakdown = useMemo(() => getFacilityBreakdown(), [spendDetailsDataFromStore, clientCostsFromStore, laborcost, totalScalCount, situation, existingMode]);
    const facilityTotals = useMemo(() => ({
        cycleCount: facilityBreakdown.reduce((s, f) => s + f.cycleCount, 0),
        callDown: facilityBreakdown.reduce((s, f) => s + f.callDown, 0),
        overstockReduction: facilityBreakdown.reduce((s, f) => s + f.overstockReduction, 0),
        expirationReduction: facilityBreakdown.reduce((s, f) => s + f.expirationReduction, 0),
        orderLine: facilityBreakdown.reduce((s, f) => s + f.orderLine, 0),
        inventoryFee: facilityBreakdown.reduce((s, f) => s + f.inventoryFee, 0),
        total: facilityBreakdown.reduce((s, f) => s + f.total, 0),
    }), [facilityBreakdown]);

    const showLaborDetail = investmentSummaryFromStore["showLaborSavingsDetail"] !== "";
    const showDelayCostOnPdf = investmentSummaryFromStore["showDelayCostOnPdf"] !== "";

    // Reasonableness guardrail: Daily Scanning Labor Savings should never exceed
    // a small fraction of the supply spend under management. When it does, it
    // almost always means the "Avg. Order Days per Week (per location)" input
    // was entered as an aggregate (summed across locations) instead of per
    // location, which inflates the labor formula by orders of magnitude.
    const dailyScanningLaborWarning = useMemo(() => {
        const cycleSavings = facilityTotals.cycleCount;
        if (totalAnnualSupplySpend <= 0 || cycleSavings <= 0) return null;
        const pctOfSpend = (cycleSavings / totalAnnualSupplySpend) * 100;
        if (pctOfSpend < 10) return null;
        const offending = facilityBreakdown
            .map((f) => {
                const facility = spendDetailsDataFromStore[
                    spendRows.find((k) => spendDetailsDataFromStore[k]?.["Facility Name"] === f.name)
                    ?? ""
                ];
                const freq = Number(facility?.["Inventory Count Frequency per Week"] ?? 0);
                return { name: f.name, freq };
            })
            .filter((f) => f.freq > 7);
        return { pctOfSpend, offending };
    }, [facilityTotals.cycleCount, totalAnnualSupplySpend, facilityBreakdown, spendDetailsDataFromStore, spendRows]);

    const showNewScaleSavings = newScaleCount > 0;
    const skuReductionPct = Number(investmentSummaryFromStore[SKU_REDUCTION_KEY] ?? 0);
    const showSkuReduction = par360Selected && skuReductionPct > 0;
    const overstockLabel = existingMode && par360Selected
        ? "Existing Overstock Reduction (PAR 360)"
        : "Overstock Reduction";
    const expirationLabel = existingMode && par360Selected
        ? "Expiration Avoidance (PAR 360)"
        : "Expiration Avoidance";
    const softwareLabel = existingMode && par360Selected
        ? "Eliminated Redundant Software (PAR 360)"
        : "Eliminated Pre-PAR Solution & Service Expenses";
    const investmentLabel = isPar360AddOn
        ? "Annual Investment in PAR 360"
        : "Annual Investment in PAR Technology";

    function getSavingTable() {
        const inventoryFee = getInventoryFeeAvoidance();
        // In add-on mode call-downs are always their own PAR 360 line, so do not
        // fold labor into the software-replacement row.
        const foldLaborIntoSoftware = !showLaborDetail && !existingMode;
        const eliminatedRow = foldLaborIntoSoftware
            ? inventoryFee.map((val, i) => val + getCycleCountLaborSavings()[i] + getCallDownLaborSavings()[i])
            : inventoryFee;

        const rows = [];
        if (showLaborDetail && showNewScaleSavings) {
            rows.push(["Daily Scanning Labor Savings", ...getCycleCountLaborSavings()]);
        }
        if (showLaborDetail && !existingMode) {
            rows.push(["Call Down Labor Savings", ...getCallDownLaborSavings()]);
        }
        if (existingMode) {
            rows.push(["Call Down & Workflow Labor Savings (PAR 360)", ...getCallDownLaborSavings()]);
        }
        rows.push([overstockLabel, ...getOverstockReduction()]);
        rows.push([expirationLabel, ...getExpirationAvoidance()]);
        if (showNewScaleSavings) {
            rows.push(["Order Line Expense Reduction", ...getOrderLineAvoidance()]);
        }
        if (showSkuReduction) {
            rows.push(["SKU Reduction (Non-Mover Overstock)", ...getSkuReductionSavings()]);
        }
        if (isPar360Premium) {
            rows.push(["Freight Savings (Package Delivery)", ...getFreightSavings()]);
        }
        rows.push([softwareLabel, ...eliminatedRow]);
        return rows;
    }

    function getUnifiedTable() {
        const inventoryFee = getInventoryFeeAvoidance();
        const foldLaborIntoSoftware = !showLaborDetail && !existingMode;
        const eliminatedRow = foldLaborIntoSoftware
            ? inventoryFee.map((val, i) => val + getCycleCountLaborSavings()[i] + getCallDownLaborSavings()[i])
            : inventoryFee;

        const savingsRows = [];
        if (showLaborDetail && showNewScaleSavings) {
            savingsRows.push({ label: "Daily Scanning Labor Savings", values: getCycleCountLaborSavings(), section: "savings" });
        }
        if (showLaborDetail && !existingMode) {
            savingsRows.push({ label: "Call Down Labor Savings", values: getCallDownLaborSavings(), section: "savings" });
        }
        if (existingMode) {
            savingsRows.push({ label: "Call Down & Workflow Labor Savings (PAR 360)", values: getCallDownLaborSavings(), section: "savings" });
        }
        savingsRows.push({ label: overstockLabel, values: getOverstockReduction(), section: "savings" });
        savingsRows.push({ label: expirationLabel, values: getExpirationAvoidance(), section: "savings" });
        if (showNewScaleSavings) {
            savingsRows.push({ label: "Order Line Expense Reduction", values: getOrderLineAvoidance(), section: "savings" });
        }
        if (showSkuReduction) {
            savingsRows.push({ label: "SKU Reduction (Non-Mover Overstock)", values: getSkuReductionSavings(), section: "savings" });
        }
        if (isPar360Premium) {
            savingsRows.push({ label: "Freight Savings (Package Delivery)", values: getFreightSavings(), section: "savings" });
        }
        savingsRows.push({ label: softwareLabel, values: eliminatedRow, section: "savings" });

        return [
            { label: investmentLabel, values: getAnnualExpense(), section: "investment" },
            ...savingsRows,
            { label: "Annual ROI", values: getAnnualROI(), section: "roi" },
            { label: "Annual ROI %", values: getAnnualROI().map((roi, i) => {
                const expense = getAnnualExpense()[i];
                return expense > 0 ? (roi / expense) * 100 : 0;
            }), section: "roiPct" },
        ];
    }

    const savingsTable = useMemo(() => getSavingTable(), [roiTerm, spendDetailsDataFromStore, clientCostsFromStore, laborcost, totalScalCount, showLaborDetail, overstockRecoveryYears, investmentSummaryFromStore, isPar360Premium, isPar360AddOn, existingMode, newScaleCount, showSkuReduction, situation]);
    const savingsTotals = useMemo(() => ["Annual Savings Total", ...getSavingsTotals()], [roiTerm, spendDetailsDataFromStore, clientCostsFromStore, laborcost, totalScalCount, overstockRecoveryYears, investmentSummaryFromStore, isPar360Premium, isPar360AddOn, existingMode, newScaleCount, showSkuReduction, situation]);
    const annualRoiTotals = useMemo(() => ["Annual ROI (Savings - Costs)", ...getAnnualROI()], [roiTerm, spendDetailsDataFromStore, clientCostsFromStore, laborcost, totalScalCount, investmentTotal, annualSupportEst, overstockRecoveryYears, investmentSummaryFromStore, isPar360Premium, isPar360AddOn, existingMode, newScaleCount, showSkuReduction, situation]);
    const investmentTable = useMemo(
        () => [
            [investmentLabel, ...getAnnualExpense()],
        ],
        [roiTerm, investmentTotal, annualSupportEst, investmentLabel]
    );
    const termNetBenefit = useMemo(() => getTermNetBenefit(), [annualRoiTotals]);
    const paybackMonths = useMemo(() => getPaybackMonths(), [annualRoiTotals, roiTerm, investmentTotal]);
    const annualRoiPctYear1 = getAnnualRoiPctYear1();
    const feesAsPercentSpend = getFeesAsPercentSpend();
    const unifiedTableData = useMemo(() => getUnifiedTable(), [roiTerm, spendDetailsDataFromStore, clientCostsFromStore, laborcost, totalScalCount, investmentTotal, annualSupportEst, showLaborDetail, overstockRecoveryYears, investmentSummaryFromStore, isPar360Premium, isPar360AddOn, existingMode, newScaleCount, showSkuReduction, situation]);

    useEffect(() => {
        dispatch(setTotals({ name: "savingsTable", value: savingsTable }));
        dispatch(setTotals({ name: "savingsTotals", value: savingsTotals }));
        dispatch(setTotals({ name: "investmentTable", value: investmentTable }));
        dispatch(setTotals({ name: "annualRoiTotals", value: annualRoiTotals }));
        dispatch(setTotals({ name: "paybackMonths", value: paybackMonths ?? 0 }));
        dispatch(setTotals({ name: "termNetBenefit", value: termNetBenefit }));
        dispatch(setTotals({ name: "annualRoiPctYear1", value: annualRoiPctYear1 }));
        dispatch(setTotals({ name: "feesAsPercentSpendUnderManagement", value: feesAsPercentSpend }));
        dispatch(setTotals({ name: "overstockImpactPct", value: overstockImpactPct }));
        dispatch(setTotals({ name: "overstockRecoveryYears", value: overstockRecoveryYears }));
        dispatch(setTotals({ name: "costOfCapitalPct", value: costOfCapitalRate * 100 }));
        dispatch(setTotals({ name: "expirationImpactPct", value: expirationImpactPct }));
        dispatch(setTotals({ name: "callDownReductionPct", value: callDownReductionPct }));
        dispatch(setTotals({ name: "annualSupplyExpenseIncreasePct", value: supplyExpenseIncreasePct }));
        dispatch(setTotals({ name: "isPar360AddOn", value: isPar360AddOn }));
        dispatch(setTotals({ name: "customerSituation", value: situation }));
        dispatch(setTotals({ name: "newScaleCount", value: newScaleCount }));
        dispatch(setTotals({ name: "existingScaleCoveragePct", value: existingScaleCoveragePct }));
        dispatch(setTotals({
            name: "Annual PAR Subscription",
            value: (scaleSubscription * (existingMode ? newScaleCount : totalScalCount) * periodsPerYear),
        }));
        dispatch(setTotals({ name: "Annual Software Subscription", value: (par360Subscription * periodsPerYear) }));
        dispatch(setTotals({ name: "Annual 3rd-Party Subscription", value: 0 }));
        dispatch(setTotals({ name: "Annual Support / Managed Services", value: annualSupportEst }));
        dispatch(setTotals({ name: "oneTimeOnboarding", value: investmentTotal }));
        dispatch(setTotals({ name: "Annual Escalator", value: 5.0 }));
        try { dispatch(setTotals({ name: "solutionSubscriptions", value: JSON.parse(solutionSubscriptionsJson) })); } catch { /* ignore parse errors */ }
        dispatch(setTotals({ name: "servicePricingDisplay", value: servicePricingDisplay }));
        dispatch(setTotals({ name: "delayCostPerDay", value: showDelayCostCallout ? delayCostPerDay : 0 }));
        dispatch(setTotals({ name: "showDelayCostOnPdf", value: showDelayCostCallout && showDelayCostOnPdf ? "1" : "" }));
    }, [dispatch, savingsTable, savingsTotals, investmentTable, annualRoiTotals, paybackMonths, termNetBenefit, annualRoiPctYear1, feesAsPercentSpend,
        overstockImpactPct, overstockRecoveryYears, expirationImpactPct, callDownReductionPct, supplyExpenseIncreasePct, costOfCapitalRate,
        scaleSubscription, totalScalCount, par360Subscription, periodsPerYear, annualSupportEst, investmentTotal, isPar360AddOn, situation, newScaleCount, existingMode, existingScaleCoveragePct,
        solutionSubscriptionsJson, servicePricingDisplay, delayCostPerDay, showDelayCostCallout, showDelayCostOnPdf]);

    const barLabels = Array.from(
        { length: roiTerm },
        (_, index) => `Year ${index + 1}`
    );

    const annualChartData = {
        labels: barLabels,
        datasets: [
            {
                label: "Annual Net Savings",
                data: getAnnualROI(),
                backgroundColor: getAnnualROI().map((v) => v >= 0 ? "rgba(16, 185, 129, 0.85)" : "rgba(239, 68, 68, 0.8)"),
                borderColor: getAnnualROI().map((v) => v >= 0 ? "#059669" : "#DC2626"),
                borderWidth: 1,
                borderRadius: 4,
            },
            {
                label: "Annual Investment",
                data: getAnnualExpense().map((v) => Math.abs(v)),
                backgroundColor: "rgba(99, 102, 241, 0.75)",
                borderColor: "#4F46E5",
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const cumulativeChartData = {
        labels: [...barLabels, "Total"],
        datasets: [
            {
                label: "Net Savings",
                data: [...getAnnualROI(), getTermNetBenefit()],
                backgroundColor: [
                    ...getAnnualROI().map((v) => v >= 0 ? "rgba(59, 130, 246, 0.8)" : "rgba(239, 68, 68, 0.8)"),
                    "rgba(245, 158, 11, 0.85)",
                ],
                borderColor: [
                    ...getAnnualROI().map((v) => v >= 0 ? "#2563EB" : "#DC2626"),
                    "#D97706",
                ],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                display: chartView === "annual",
                labels: {
                    usePointStyle: true,
                    pointStyle: "rect",
                    generateLabels: (chart) => {
                        const datasets = chart.data.datasets;
                        return datasets.map((ds, i) => ({
                            text: ds.label,
                            fillStyle: i === 0 ? "#10B981" : "#6366F1",
                            strokeStyle: i === 0 ? "#059669" : "#4F46E5",
                            hidden: !chart.isDatasetVisible(i),
                            datasetIndex: i,
                            pointStyle: "rect",
                        }));
                    },
                },
            },
            title: {
                display: true,
                text: chartView === "annual" ? "Annual Net Savings vs. Investment" : "Net Savings by Year",
                font: { size: 16, weight: "bold" as const },
                padding: { bottom: 20 },
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.parsed.y ?? 0;
                        const abs = Math.abs(value);
                        const formatted = abs.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            maximumFractionDigits: 0,
                        });
                        const prefix = context.dataset.label ? `${context.dataset.label}: ` : "";
                        return `${prefix}${value < 0 ? `-${formatted}` : formatted}`;
                    },
                },
            },
        },
        scales: {
            y: {
                grid: {
                    color: (context) =>
                        context.tick.value === 0 ? "#000" : "rgba(0, 0, 0, 0.1)",
                    lineWidth: (context) => (context.tick.value === 0 ? 2 : 1),
                },
                ticks: {
                    callback: (value) => formatCurrency(Number(value)),
                },
            },
            x: {
                grid: {
                    display: false,
                },
            },
        },
    };

    const handleChange = (event: MouseEvent<HTMLElement>, roiTerm: number) => {
        if (roiTerm != null) setRoiTerm(roiTerm);
    };

    const handleChartViewChange = (_event: MouseEvent<HTMLElement>, newView: "annual" | "cumulative") => {
        if (newView != null) setChartView(newView);
    };

    return (
        <Slide direction="right" in={true} mountOnEnter unmountOnExit>
            <div style={{ textAlign: "center" }}>
                <h1>{situationTitle(situation)}</h1>
                {isPar360AddOn && (
                    <Alert severity="info" sx={{ marginBottom: "20px", textAlign: "left" }}>
                        This is an incremental analysis for adding PAR 360 to hospitals that
                        already have PAR Scales. Daily scanning labor, order-line savings, and
                        existing scale subscription fees are excluded. Remaining overstock,
                        expirations, call-downs/workflow, redundant software, and freight
                        (Premium) are counted as PAR 360 value.
                    </Alert>
                )}
                {situation === CUSTOMER_SITUATION.MIXED && (
                    <Alert severity="info" sx={{ marginBottom: "20px", textAlign: "left" }}>
                        Mixed system: unscaled hospitals get a full scale ROI. Already-scaled
                        hospitals contribute PAR 360 value only. Scale subscription is charged
                        on new scales only.
                    </Alert>
                )}
                {situation === CUSTOMER_SITUATION.SCALE_EXPANSION && (
                    <Alert severity="info" sx={{ marginBottom: "20px", textAlign: "left" }}>
                        Scale expansion: labor and order-line savings are from new scales only.
                        PAR 360 value applies to the footprint. CAPEX is Year 1, so Year 1 can
                        look weaker than years 2–5.
                    </Alert>
                )}
                {existingMode && existingScaleCoveragePct > 0 && (
                    <Alert severity="info" sx={{ marginBottom: "20px", textAlign: "left" }}>
                        Overstock and expiration savings exclude{" "}
                        <strong>{existingScaleCoveragePct.toFixed(0)}%</strong> of supply spend
                        already covered by existing scales (existing scales ÷ Total SKUs for each
                        already-scaled facility). Those savings are already in the customer's baseline.
                    </Alert>
                )}
                {dailyScanningLaborWarning && showNewScaleSavings && (
                    <Alert
                        severity="warning"
                        sx={{ marginBottom: "20px", textAlign: "left" }}
                    >
                        <strong>Daily Scanning Labor Savings look unusually high</strong>{" "}
                        ({dailyScanningLaborWarning.pctOfSpend.toFixed(1)}% of annual supply spend under management).{" "}
                        {dailyScanningLaborWarning.offending.length > 0 ? (
                            <>
                                The most common cause is the{" "}
                                <em>"Avg. Order Days per Week (per location)"</em> field being entered as an aggregate
                                across locations. The following facilit
                                {dailyScanningLaborWarning.offending.length === 1 ? "y has" : "ies have"} a value greater than 7:{" "}
                                {dailyScanningLaborWarning.offending
                                    .map((f) => `${f.name || "(unnamed)"} = ${f.freq}`)
                                    .join("; ")}
                                . Please re-check that field on the Spend Details by Facility step
                                — it should be the days per week each location is counted (typically 5, max 7).
                            </>
                        ) : (
                            <>
                                Please re-check the inputs on the Spend Details by Facility step (especially
                                "Avg. Order Days per Week (per location)" and the scale count) to confirm
                                the labor savings number is reasonable.
                            </>
                        )}
                    </Alert>
                )}
                <h3>Year 1 Savings by Facility</h3>
                <TableContainer component={Paper} sx={{ marginBottom: "30px" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ fontWeight: 600 }}>Facility</TableCell>
                                {showNewScaleSavings && (
                                    <TableCell style={{ fontWeight: 600 }} align="right">Daily Scanning Labor</TableCell>
                                )}
                                <TableCell style={{ fontWeight: 600 }} align="right">
                                    {existingMode ? "Call Down & Workflow Labor" : "Call Down Labor"}
                                </TableCell>
                                <TableCell style={{ fontWeight: 600 }} align="right">
                                    {existingMode && par360Selected ? "Existing Overstock (PAR 360)" : "Overstock Reduction"}
                                </TableCell>
                                <TableCell style={{ fontWeight: 600 }} align="right">Expiration Avoidance</TableCell>
                                {showNewScaleSavings && (
                                    <TableCell style={{ fontWeight: 600 }} align="right">Order Line Expense</TableCell>
                                )}
                                <TableCell style={{ fontWeight: 600 }} align="right">
                                    {existingMode && par360Selected ? "Redundant Software (PAR 360)" : "Pre-PAR Solution & Service Expenses"}
                                </TableCell>
                                <TableCell style={{ fontWeight: 600 }} align="right">Total Savings</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {facilityBreakdown.map((row) => {
                                const cells = showNewScaleSavings
                                    ? [row.cycleCount, row.callDown, row.overstockReduction, row.expirationReduction, row.orderLine, row.inventoryFee, row.total]
                                    : [row.callDown, row.overstockReduction, row.expirationReduction, row.inventoryFee, row.total];
                                return (
                                <TableRow hover key={row.name}>
                                    <TableCell>{row.name || "(unnamed)"}</TableCell>
                                    {cells.map((val, i) => (
                                        <TableCell align="right" key={i}>
                                            <NumberFormat displayType="text" thousandSeparator="," prefix="$" decimalScale={0} type="text" value={displayNumber(val)} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                                );
                            })}
                            <TableRow sx={{ "& td, & th": { borderTop: 1, fontWeight: 600 } }}>
                                <TableCell>IDN Total</TableCell>
                                {(showNewScaleSavings
                                    ? [facilityTotals.cycleCount, facilityTotals.callDown, facilityTotals.overstockReduction, facilityTotals.expirationReduction, facilityTotals.orderLine, facilityTotals.inventoryFee, facilityTotals.total]
                                    : [facilityTotals.callDown, facilityTotals.overstockReduction, facilityTotals.expirationReduction, facilityTotals.inventoryFee, facilityTotals.total]
                                ).map((val, i) => (
                                    <TableCell align="right" key={i}>
                                        <NumberFormat displayType="text" thousandSeparator="," prefix="$" decimalScale={0} type="text" value={displayNumber(val)} />
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                <div style={{ marginBottom: "30px" }}>
                    <div style={{ marginBottom: "15px", fontSize: "15px", fontWeight: 600 }}>
                        ROI Analysis Term:
                    </div>
                    <ToggleButtonGroup
                        color="primary"
                        value={roiTerm}
                        exclusive
                        onChange={handleChange}
                    >
                        <ToggleButton value={5}>5 Years</ToggleButton>
                        <ToggleButton value={7}>7 Years</ToggleButton>
                        <ToggleButton value={10}>10 Years</ToggleButton>
                    </ToggleButtonGroup>
                </div>

                <h3>Annual Impact &amp; ROI</h3>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ fontWeight: 600, minWidth: 280 }}> </TableCell>
                                {Array.from({ length: roiTerm }, (_, index) => (
                                    <TableCell
                                        key={index}
                                        style={{ fontWeight: 700, fontSize: "0.95rem" }}
                                        align="right"
                                        sx={yearColumnSx(index + 1)}
                                    >
                                        Year {index + 1}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {unifiedTableData.map((row, rowIdx) => {
                                const isInvestment = row.section === "investment";
                                const isRoi = row.section === "roi";
                                const isRoiPct = row.section === "roiPct";
                                const isFirstSavings = rowIdx > 0 && row.section === "savings" && unifiedTableData[rowIdx - 1].section === "investment";
                                const isRoiSeparator = isRoi;

                                return (
                                    <React.Fragment key={row.label}>
                                        {isFirstSavings && (
                                            <TableRow>
                                                <TableCell colSpan={roiTerm + 1} sx={{ py: 0.5, borderBottom: "none" }}>&nbsp;</TableCell>
                                            </TableRow>
                                        )}
                                        {isRoiSeparator && (
                                            <TableRow>
                                                <TableCell colSpan={roiTerm + 1} sx={{ py: 0.5, borderBottom: "none" }}>&nbsp;</TableCell>
                                            </TableRow>
                                        )}
                                        <TableRow
                                            hover={!(isRoi || isRoiPct)}
                                            sx={{
                                                ...((isRoi || isRoiPct) && { "& td": { fontWeight: 700, fontSize: "0.95rem" } }),
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    fontWeight: (isInvestment || isRoi || isRoiPct) ? 700 : 400,
                                                    ...(isInvestment && { fontStyle: "italic" }),
                                                }}
                                            >
                                                {row.label}
                                            </TableCell>
                                            {row.values.map((val, colIdx) => (
                                                <TableCell
                                                    key={colIdx}
                                                    align="right"
                                                    sx={{
                                                        ...yearColumnSx(colIdx + 1),
                                                        fontWeight: (isInvestment || isRoi || isRoiPct) ? 700 : 400,
                                                        ...(isInvestment && { backgroundColor: "#e3f2fd" }),
                                                        ...((isRoi || isRoiPct) && { backgroundColor: "#e8f5e9" }),
                                                    }}
                                                >
                                                    {isRoiPct ? (
                                                        `${displayNumber(val).toFixed(0)}%`
                                                    ) : (
                                                        <NumberFormat
                                                            displayType="text"
                                                            thousandSeparator={","}
                                                            prefix={isInvestment ? "($" : "$"}
                                                            suffix={isInvestment ? ")" : ""}
                                                            decimalScale={0}
                                                            type="text"
                                                            value={Math.abs(displayNumber(val))}
                                                        />
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {showDelayCostCallout && (
                    <div style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "32px",
                        padding: "18px 20px",
                        marginTop: "2px",
                        marginBottom: "28px",
                        background: "#EAF1FB",
                        borderTop: "2px solid #003F81",
                        borderBottom: "2px solid #003F81",
                        boxSizing: "border-box",
                    }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: "#003F81", fontSize: "0.95rem", fontWeight: 600, lineHeight: 1.3 }}>
                                Your estimated cost of delaying supply chain modernization?
                            </div>
                            <div style={{ color: "#555", fontSize: "0.78rem", marginTop: "5px", fontWeight: 400, lineHeight: 1.5 }}>
                                Daily cost of delaying investment in your supply chain improvement opportunities given your overstock levels, expiration rates &amp; cost of capital.
                            </div>
                        </div>
                        <div style={{ color: "#003F81", fontSize: "2.0rem", fontWeight: 800, whiteSpace: "nowrap", letterSpacing: "-0.5px", flexShrink: 0 }}>
                            <NumberFormat
                                displayType="text"
                                thousandSeparator=","
                                prefix="$"
                                decimalScale={0}
                                type="text"
                                value={Math.round(delayCostPerDay)}
                            />{" "}per day
                        </div>
                    </div>
                )}

                <div style={{ width: "70%", margin: "30px auto 0" }}>
                    <ToggleButtonGroup
                        color="primary"
                        value={chartView}
                        exclusive
                        onChange={handleChartViewChange}
                        size="small"
                        sx={{ marginBottom: "20px" }}
                    >
                        <ToggleButton value="annual">Annual Net Savings</ToggleButton>
                        <ToggleButton value="cumulative">Cumulative</ToggleButton>
                    </ToggleButtonGroup>
                    <Bar
                        options={chartOptions}
                        data={chartView === "annual" ? annualChartData : cumulativeChartData}
                    />
                </div>
                <div style={{ marginTop: "30px", fontSize: "0.85rem", color: "#666", textAlign: "left", width: "70%", margin: "30px auto 0" }}>
                    <p>
                        The {numberOfLocations} facilit{numberOfLocations !== 1 ? "ies" : "y"} included in this{" "}
                        {existingMode ? "incremental " : ""}analysis{" "}
                        {numberOfLocations === 1 ? "captures" : "capture"}{" "}
                        <NumberFormat
                            displayType="text"
                            thousandSeparator={","}
                            prefix="$"
                            decimalScale={0}
                            type="text"
                            value={totalAnnualSupplySpend}
                        />{" "}
                        in annual supply spend under management.
                    </p>
                </div>
            </div>
        </Slide>
    );
};
